import React, { useState, useRef, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Check, X, Pencil } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * InlineEdit - A component for inline editing of text fields
 * 
 * @param {string} value - Current value
 * @param {function} onSave - Callback when saving (receives new value)
 * @param {string} type - 'text', 'textarea', 'number', 'currency'
 * @param {string} placeholder - Placeholder text
 * @param {boolean} disabled - Disable editing
 * @param {string} className - Additional classes for the display text
 * @param {string} emptyText - Text to show when value is empty
 */
export const InlineEdit = ({ 
  value, 
  onSave, 
  type = 'text',
  placeholder = 'Click to edit',
  disabled = false,
  className = '',
  emptyText = 'Not set',
  prefix = '',
  suffix = ''
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value || '');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  useEffect(() => {
    setEditValue(value || '');
  }, [value]);

  const handleSave = async () => {
    if (editValue === value) {
      setIsEditing(false);
      return;
    }
    
    setSaving(true);
    try {
      await onSave(type === 'number' || type === 'currency' ? parseFloat(editValue) || 0 : editValue);
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to save:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditValue(value || '');
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && type !== 'textarea') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  const formatDisplayValue = () => {
    if (!value && value !== 0) return emptyText;
    if (type === 'currency') return `${prefix}${Number(value).toLocaleString()}${suffix}`;
    if (type === 'number') return `${prefix}${value}${suffix}`;
    return `${prefix}${value}${suffix}`;
  };

  if (disabled) {
    return (
      <span className={className}>
        {formatDisplayValue()}
      </span>
    );
  }

  if (isEditing) {
    const InputComponent = type === 'textarea' ? Textarea : Input;
    
    return (
      <div className="flex items-center gap-2 -my-1">
        <InputComponent
          ref={inputRef}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={() => setTimeout(() => !saving && handleCancel(), 150)}
          type={type === 'number' || type === 'currency' ? 'number' : 'text'}
          placeholder={placeholder}
          className={cn(
            "h-8 text-sm",
            type === 'textarea' && "min-h-[60px] py-2"
          )}
          disabled={saving}
        />
        <div className="flex gap-1">
          <Button 
            size="icon" 
            variant="ghost" 
            className="h-7 w-7 text-green-600 hover:text-green-700 hover:bg-green-50"
            onClick={handleSave}
            disabled={saving}
          >
            <Check className="h-4 w-4" />
          </Button>
          <Button 
            size="icon" 
            variant="ghost" 
            className="h-7 w-7 text-slate-500 hover:text-slate-700"
            onClick={handleCancel}
            disabled={saving}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <span 
      className={cn(
        "group cursor-pointer inline-flex items-center gap-1 hover:bg-slate-100 px-1 -mx-1 rounded transition-colors",
        !value && value !== 0 && "text-slate-400 italic",
        className
      )}
      onClick={() => setIsEditing(true)}
      data-testid="inline-edit-trigger"
    >
      {formatDisplayValue()}
      <Pencil className="h-3 w-3 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />
    </span>
  );
};

export default InlineEdit;
